import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calculator,
  Check,
  FileText,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ApiError, apiFetch, apiFetchBlob } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

// ============================ types ============================

/** Retorno do lookup de CNPJ na BrasilAPI. */
interface BrasilApiResult {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
  email?: string;
  telefone?: string;
}

type ProposalStatus =
  | 'DRAFT'
  | 'SENT'
  | 'VIEWED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'REVISED'
  | 'EXPIRED'
  | 'CANCELLED';

interface Proposal {
  id: string;
  numero: number;
  status: ProposalStatus;
  nomeServico: string | null;
  valorPrincipal: string | number;
  valorUnidade: string | null;
  dataEmissao: string;
  validadeDias: number;
  responsavelNome: string | null;
  responsavelTratamento: string | null;
  leadName: string | null;
  leadEmail: string | null;
  client?: { id: string; razaoSocial: string; email: string | null } | null;
  service?: { id: string; codigo: string; nome: string };
  issuerCompany?: { id: string; razaoSocial: string; nomeFantasia: string | null };
}

interface ListResponse {
  rows: Proposal[];
  total: number;
}

type ChargeLineKind = 'UNICA' | 'MENSAL' | 'ANUAL';

interface ChargeLine {
  descricao: string;
  valor: number;
  tipo: ChargeLineKind;
  notaCalculo?: string;
  /** Linha de referencia: mostra o valor no PDF mas nao soma nos totais. */
  informativa?: boolean;
  /** Selo opcional ao lado da descricao (ex.: "ja vigente"). Vazio = sem selo. */
  selo?: string;
}

interface ServiceMini {
  id: string;
  codigo: string;
  nome: string;
  subtitulo: string | null;
  precoBase: string | number | null;
  precoUnidade: string | null;
  precoNota: string | null;
  precoSubnota: string | null;
  validadeDiasDefault: number;
  chargeLines: ChargeLine[] | null;
  issuerCompanyId: string | null;
}

/** Modulo do combo: `incluso: false` sai no PDF como disponivel (nao contratado). */
interface ProposalModule {
  serviceId?: string;
  codigo?: string;
  nome: string;
  /** Snapshot do subtitulo do servico — descreve o modulo no PDF. */
  descricao?: string;
  /** Prazo de entrega mostrado no card do PDF (ex.: "10 dias"). */
  prazo?: string;
  incluso: boolean;
}

interface ClientMini {
  id: string;
  razaoSocial: string;
  cpfCnpj: string;
  email: string | null;
}

const STATUS_LABEL: Record<ProposalStatus, string> = {
  DRAFT: 'Rascunho',
  SENT: 'Enviada',
  VIEWED: 'Visualizada',
  ACCEPTED: 'Aceita',
  REJECTED: 'Recusada',
  REVISED: 'Revisada',
  EXPIRED: 'Expirada',
  CANCELLED: 'Cancelada',
};

const STATUS_STYLE: Record<ProposalStatus, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SENT: 'bg-blue-500/10 text-blue-600',
  VIEWED: 'bg-indigo-500/10 text-indigo-600',
  ACCEPTED: 'bg-emerald-500/10 text-emerald-600',
  REJECTED: 'bg-destructive/10 text-destructive',
  REVISED: 'bg-amber-500/10 text-amber-600',
  EXPIRED: 'bg-muted text-muted-foreground',
  CANCELLED: 'bg-destructive/10 text-destructive',
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

// ============================ page ============================

export function ProposalsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ProposalStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Proposal | null>(null);
  const [accepting, setAccepting] = useState<Proposal | null>(null);
  const [rejecting, setRejecting] = useState<Proposal | null>(null);
  const [revising, setRevising] = useState<Proposal | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['proposals', { statusFilter, search }],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '100' });
      if (statusFilter !== 'ALL') params.set('status', statusFilter);
      if (search) params.set('search', search);
      return apiFetch<ListResponse>(`/proposals?${params}`);
    },
  });

  const sendEmail = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ sent: boolean; to: string }>(`/proposals/${id}/send-email`, { method: 'POST' }),
    onSuccess: (r) => {
      alert(`Email enviado para ${r.to}`);
      qc.invalidateQueries({ queryKey: ['proposals'] });
    },
    onError: (err) =>
      alert(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => apiFetch(`/proposals/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proposals'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/proposals/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proposals'] }),
  });

  async function openPdf(id: string) {
    try {
      const blob = await apiFetchBlob(`/proposals/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        // Bloqueado por popup blocker → força download
        const a = document.createElement('a');
        a.href = url;
        a.download = `proposta-${id}.pdf`;
        a.click();
      }
      // Libera memoria depois de 1 minuto
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      alert(
        err instanceof ApiError
          ? `Erro ${err.status}: ${err.body}`
          : err instanceof Error
            ? err.message
            : 'Falha ao gerar PDF',
      );
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Propostas</h1>
          <p className="text-sm text-muted-foreground">
            Gera PDF, envia por email e ao ser aceita cria lançamento financeiro
            para emitir nota.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Nova proposta
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder="Buscar por cliente, lead ou serviço…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[280px] rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProposalStatus | 'ALL')}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="ALL">Todos</option>
          {(Object.keys(STATUS_LABEL) as ProposalStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {data && data.rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <ScrollText className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhuma proposta.</p>
          </div>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Nº</th>
                <th className="px-4 py-2 font-medium">Cliente / Lead</th>
                <th className="px-4 py-2 font-medium">Serviço</th>
                <th className="px-4 py-2 font-medium">Valor</th>
                <th className="px-4 py-2 font-medium">Emissão</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                  <td className="px-4 py-2 font-mono text-xs">#{p.numero}</td>
                  <td className="px-4 py-2 font-medium">
                    {p.client?.razaoSocial ?? p.leadName ?? '—'}
                    {(p.responsavelNome || p.responsavelTratamento) && (
                      <span className="block text-xs text-muted-foreground">
                        A/C {p.responsavelTratamento} {p.responsavelNome}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{p.nomeServico ?? p.service?.nome ?? '—'}</td>
                  <td className="px-4 py-2">
                    {formatBRL(Number(p.valorPrincipal))}
                    {p.valorUnidade && (
                      <span className="text-xs text-muted-foreground">{p.valorUnidade}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{fmtDate(p.dataEmissao)}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[p.status]}`}
                    >
                      {STATUS_LABEL[p.status]}
                    </span>
                  </td>
                  <td className="px-2 py-2 flex justify-end gap-1">
                    <button
                      type="button"
                      data-dica="Ver PDF"
                      onClick={() => openPdf(p.id)}
                      className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    {(p.status === 'DRAFT' || p.status === 'SENT' || p.status === 'VIEWED') && (
                      <>
                        <button
                          type="button"
                          data-dica="Editar"
                          onClick={() => setEditing(p)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Enviar por email"
                          onClick={() => {
                            if (confirm('Enviar proposta por email?')) sendEmail.mutate(p.id);
                          }}
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Aceitar proposta"
                          onClick={() => setAccepting(p)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-600"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Contra-proposta"
                          onClick={() => setRevising(p)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-amber-500/10 hover:text-amber-600"
                        >
                          <Calculator className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Recusar"
                          onClick={() => setRejecting(p)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Cancelar proposta"
                          onClick={() => {
                            if (confirm('Cancelar proposta?')) cancel.mutate(p.id);
                          }}
                          className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {(p.status === 'REJECTED' || p.status === 'CANCELLED' || p.status === 'EXPIRED') && (
                      <button
                        type="button"
                        data-dica="Excluir"
                        onClick={() => {
                          if (confirm('Excluir proposta?')) remove.mutate(p.id);
                        }}
                        className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showCreate && (
        <ProposalFormDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['proposals'] });
          }}
        />
      )}
      {editing && (
        <ProposalFormDialog
          proposal={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['proposals'] });
          }}
        />
      )}
      {accepting && (
        <AcceptDialog
          proposal={accepting}
          onClose={() => setAccepting(null)}
          onDone={() => {
            setAccepting(null);
            qc.invalidateQueries({ queryKey: ['proposals'] });
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}
      {rejecting && (
        <RejectDialog
          proposal={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            qc.invalidateQueries({ queryKey: ['proposals'] });
          }}
        />
      )}
      {revising && (
        <ReviseDialog
          proposal={revising}
          onClose={() => setRevising(null)}
          onDone={() => {
            setRevising(null);
            qc.invalidateQueries({ queryKey: ['proposals'] });
          }}
        />
      )}
    </div>
  );
}

// ============================ Form dialog (create + edit) ============================

interface ProposalFull extends Proposal {
  leadCpfCnpj?: string | null;
  leadPhone?: string | null;
  valorNota?: string | null;
  observacoesInternas?: string | null;
  issuerCompanyId?: string | null;
  chargeLines?: ChargeLine[] | null;
  modules?: ProposalModule[] | null;
}

function ProposalFormDialog({
  proposal,
  onClose,
  onSaved,
}: {
  proposal?: Proposal;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!proposal;
  const today = new Date().toISOString().slice(0, 10);
  const full = proposal as ProposalFull | undefined;
  const initialDate = full?.dataEmissao
    ? new Date(full.dataEmissao).toISOString().slice(0, 10)
    : today;

  const [destination, setDestination] = useState<'client' | 'lead'>(
    full?.client?.id ? 'client' : full?.leadName ? 'lead' : 'client',
  );
  const [form, setForm] = useState({
    serviceId: full?.service?.id ?? '',
    clientId: full?.client?.id ?? '',
    leadName: full?.leadName ?? '',
    leadCpfCnpj: full?.leadCpfCnpj ?? '',
    leadEmail: full?.leadEmail ?? '',
    leadPhone: full?.leadPhone ?? '',
    responsavelTratamento: full?.responsavelTratamento ?? 'Sr.',
    responsavelNome: full?.responsavelNome ?? '',
    valorPrincipal: full?.valorPrincipal ? Number(full.valorPrincipal) : 0,
    valorUnidade: full?.valorUnidade ?? '',
    valorNota: full?.valorNota ?? '',
    dataEmissao: initialDate,
    validadeDias: full?.validadeDias ?? 30,
    observacoesInternas: full?.observacoesInternas ?? '',
    issuerCompanyId: full?.issuerCompanyId ?? '',
    chargeLines:
      full?.chargeLines?.map((c) => ({
        descricao: c.descricao,
        valor: Number(c.valor),
        tipo: c.tipo,
        notaCalculo: c.notaCalculo ?? '',
        informativa: c.informativa ?? false,
        selo: c.selo ?? '',
        // Marca qual modulo gerou a linha, pra remove-la ao desmarcar.
        // Nao vai pro backend — removido em cleanedChargeLines.
        srcServiceId: undefined as string | undefined,
      })) ?? ([] as Array<{
        descricao: string;
        valor: number;
        tipo: ChargeLineKind;
        notaCalculo: string;
        informativa: boolean;
        selo: string;
        srcServiceId?: string;
      }>),
    modules: (full?.modules ?? []) as ProposalModule[],
  });
  const [error, setError] = useState<string | null>(null);
  const [lookupErr, setLookupErr] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (cnpj: string) =>
      apiFetch<BrasilApiResult>(`/clients/lookup-cnpj/${cnpj.replace(/\D/g, '')}`),
    onSuccess: (r) => {
      setForm((s) => ({
        ...s,
        leadName: r.nomeFantasia || r.razaoSocial || s.leadName,
        leadEmail: r.email ?? s.leadEmail,
        leadPhone: r.telefone ?? s.leadPhone,
      }));
      setLookupErr(null);
    },
    onError: () => setLookupErr('CNPJ não encontrado na BrasilAPI.'),
  });

  const services = useQuery({
    queryKey: ['services', 'select'],
    queryFn: () => apiFetch<ServiceMini[]>('/services?isActive=true'),
  });

  const clients = useQuery({
    queryKey: ['clients', 'select-proposal'],
    queryFn: () =>
      apiFetch<{ rows: ClientMini[] }>('/clients?pageSize=200').then((r) => r.rows),
  });

  const issuers = useQuery({
    queryKey: ['issuer-companies'],
    queryFn: () =>
      apiFetch<Array<{ id: string; razaoSocial: string; isDefault: boolean }>>(
        '/issuer-companies',
      ),
  });

  function onServiceChange(serviceId: string) {
    const svc = services.data?.find((s) => s.id === serviceId);
    setForm((f) => ({
      ...f,
      serviceId,
      valorPrincipal: svc?.precoBase ? Number(svc.precoBase) : f.valorPrincipal,
      valorUnidade: svc?.precoUnidade ?? f.valorUnidade,
      validadeDias: svc?.validadeDiasDefault ?? f.validadeDias,
      // Pre-preenche empresa emissora do servico se houver
      issuerCompanyId: svc?.issuerCompanyId ?? f.issuerCompanyId,
      // Pre-preenche nota do valor (caso o servico tenha definido um padrao)
      valorNota: f.valorNota || svc?.precoNota || '',
      chargeLines:
        svc?.chargeLines && svc.chargeLines.length > 0
          ? svc.chargeLines.map((c) => ({
              descricao: c.descricao,
              valor: Number(c.valor),
              tipo: c.tipo,
              notaCalculo: c.notaCalculo ?? '',
              informativa: c.informativa ?? false,
              selo: c.selo ?? '',
            }))
          : f.chargeLines,
    }));
  }

  /**
   * Marca/desmarca um modulo do combo.
   *
   * - `incluso` = true  -> injeta as linhas de cobranca do servico, prefixadas
   *   com o nome do modulo, e marca cada linha com `srcServiceId`.
   * - `incluso` = false -> remove as linhas que vieram desse modulo, mas o item
   *   continua na lista (sai no PDF como disponivel, em cinza).
   * - remover do combo  -> passa `null`, tira o item e as linhas dele.
   */
  function toggleModule(svc: ServiceMini, incluso: boolean | null) {
    setForm((f) => {
      const semAsLinhasDele = f.chargeLines.filter(
        (c) => c.srcServiceId !== svc.id,
      );

      if (incluso === null) {
        return {
          ...f,
          modules: f.modules.filter((m) => m.serviceId !== svc.id),
          chargeLines: semAsLinhasDele,
        };
      }

      const modules = f.modules.some((m) => m.serviceId === svc.id)
        ? f.modules.map((m) => (m.serviceId === svc.id ? { ...m, incluso } : m))
        : [
            ...f.modules,
            {
              serviceId: svc.id,
              codigo: svc.codigo,
              nome: svc.nome,
              descricao: svc.subtitulo ?? undefined,
              incluso,
            },
          ];

      // So um modulo incluso gera cobranca.
      const novasLinhas =
        incluso && svc.chargeLines?.length
          ? svc.chargeLines.map((c) => ({
              descricao: `${c.descricao} — ${svc.nome}`,
              valor: Number(c.valor),
              tipo: c.tipo,
              notaCalculo: c.notaCalculo ?? '',
              informativa: c.informativa ?? false,
              selo: c.selo ?? '',
              srcServiceId: svc.id,
            }))
          : [];

      return { ...f, modules, chargeLines: [...semAsLinhasDele, ...novasLinhas] };
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const cleanedChargeLines = form.chargeLines
        .filter((c) => c.descricao && c.valor !== 0)
        .map((c) => ({
          descricao: c.descricao,
          valor: c.valor,
          tipo: c.tipo,
          notaCalculo: c.notaCalculo || undefined,
          informativa: c.informativa || undefined,
          selo: (c.informativa && c.selo) || undefined,
        }));
      const payload: Record<string, unknown> = {
        serviceId: form.serviceId,
        responsavelTratamento: form.responsavelTratamento || undefined,
        responsavelNome: form.responsavelNome || undefined,
        valorPrincipal: form.valorPrincipal,
        valorUnidade: form.valorUnidade || undefined,
        valorNota: form.valorNota || undefined,
        dataEmissao: form.dataEmissao,
        validadeDias: form.validadeDias,
        observacoesInternas: form.observacoesInternas || undefined,
        issuerCompanyId: form.issuerCompanyId || undefined,
        chargeLines: cleanedChargeLines.length > 0 ? cleanedChargeLines : undefined,
        modules: form.modules.length > 0 ? form.modules : undefined,
      };
      if (destination === 'client') {
        payload.clientId = form.clientId;
        payload.leadName = null;
        payload.leadCpfCnpj = null;
        payload.leadEmail = null;
        payload.leadPhone = null;
      } else {
        payload.clientId = null;
        payload.leadName = form.leadName;
        payload.leadCpfCnpj = form.leadCpfCnpj || null;
        payload.leadEmail = form.leadEmail || null;
        payload.leadPhone = form.leadPhone || null;
      }
      const body = JSON.stringify(payload);
      return isEdit
        ? apiFetch(`/proposals/${proposal!.id}`, { method: 'PATCH', body })
        : apiFetch('/proposals', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <Dialog title={isEdit ? `Editar proposta #${proposal!.numero}` : 'Nova proposta'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Serviço *">
          <select
            required
            className="input"
            value={form.serviceId}
            onChange={(e) => onServiceChange(e.target.value)}
          >
            <option value="">—</option>
            {services.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.codigo} — {s.nome}
              </option>
            ))}
          </select>
        </Field>

        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="flex gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={destination === 'client'}
                onChange={() => setDestination('client')}
              />
              Cliente cadastrado
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={destination === 'lead'}
                onChange={() => setDestination('lead')}
              />
              Lead (sem cadastro)
            </label>
          </div>

          {destination === 'client' && (
            <Field label="Cliente *">
              <select
                required
                className="input"
                value={form.clientId}
                onChange={(e) => setForm((s) => ({ ...s, clientId: e.target.value }))}
              >
                <option value="">—</option>
                {clients.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial} ({c.cpfCnpj})
                  </option>
                ))}
              </select>
            </Field>
          )}

          {destination === 'lead' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome *">
                <input
                  required
                  className="input"
                  value={form.leadName}
                  onChange={(e) => setForm((s) => ({ ...s, leadName: e.target.value }))}
                />
              </Field>
              <Field label="CPF/CNPJ">
                <div className="flex gap-2">
                  <input
                    className="input"
                    value={form.leadCpfCnpj}
                    onChange={(e) => setForm((s) => ({ ...s, leadCpfCnpj: e.target.value }))}
                  />
                  <button
                    type="button"
                    disabled={lookup.isPending || form.leadCpfCnpj.replace(/\D/g, '').length !== 14}
                    onClick={() => lookup.mutate(form.leadCpfCnpj)}
                    title="Buscar dados do CNPJ na BrasilAPI"
                    className="flex items-center gap-1 rounded-md border border-input bg-background px-3 text-xs whitespace-nowrap hover:bg-accent disabled:opacity-50"
                  >
                    <Search className="h-3 w-3" /> {lookup.isPending ? 'Buscando…' : 'Buscar'}
                  </button>
                </div>
                {lookupErr && <span className="text-xs text-destructive">{lookupErr}</span>}
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  className="input"
                  value={form.leadEmail}
                  onChange={(e) => setForm((s) => ({ ...s, leadEmail: e.target.value }))}
                />
              </Field>
              <Field label="Telefone">
                <input
                  className="input"
                  value={form.leadPhone}
                  onChange={(e) => setForm((s) => ({ ...s, leadPhone: e.target.value }))}
                />
              </Field>
            </div>
          )}
        </div>

        <div className="grid grid-cols-[100px_1fr] gap-3">
          <Field label="Tratamento">
            <select
              className="input"
              value={form.responsavelTratamento}
              onChange={(e) => setForm((s) => ({ ...s, responsavelTratamento: e.target.value }))}
            >
              <option value="Sr.">Sr.</option>
              <option value="Sra.">Sra.</option>
              <option value="Dr.">Dr.</option>
              <option value="Dra.">Dra.</option>
              <option value="">—</option>
            </select>
          </Field>
          <Field label="Responsável (A/C)">
            <input
              className="input"
              placeholder="Rodolpho"
              value={form.responsavelNome}
              onChange={(e) => setForm((s) => ({ ...s, responsavelNome: e.target.value }))}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Valor *">
            <input
              required
              type="number"
              step="0.01"
              min={0.01}
              className="input"
              value={form.valorPrincipal || ''}
              onChange={(e) =>
                setForm((s) => ({ ...s, valorPrincipal: Number(e.target.value) || 0 }))
              }
            />
          </Field>
          <Field label="Unidade (ex.: /mês)">
            <input
              className="input"
              value={form.valorUnidade}
              onChange={(e) => setForm((s) => ({ ...s, valorUnidade: e.target.value }))}
            />
          </Field>
          <Field label="Validade (dias)">
            <input
              type="number"
              min={1}
              className="input"
              value={form.validadeDias}
              onChange={(e) =>
                setForm((s) => ({ ...s, validadeDias: Number(e.target.value) || 30 }))
              }
            />
          </Field>
          <Field label="Data emissão">
            <input
              type="date"
              className="input"
              value={form.dataEmissao}
              onChange={(e) => setForm((s) => ({ ...s, dataEmissao: e.target.value }))}
            />
          </Field>
          <Field label="Empresa emissora">
            <select
              className="input"
              value={form.issuerCompanyId}
              onChange={(e) => setForm((s) => ({ ...s, issuerCompanyId: e.target.value }))}
            >
              <option value="">— Usar padrão —</option>
              {issuers.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razaoSocial}
                  {c.isDefault ? ' (padrão)' : ''}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nota do valor (ex.: pagamento à vista)">
          <input
            className="input"
            value={form.valorNota}
            onChange={(e) => setForm((s) => ({ ...s, valorNota: e.target.value }))}
          />
        </Field>

        <fieldset className="space-y-2 rounded-md border border-border p-3">
          <legend className="px-2 text-xs font-medium uppercase text-muted-foreground">
            Módulos e integrações do combo
          </legend>
          <p className="text-xs text-muted-foreground">
            <b>Incluso</b> soma as linhas de cobrança do módulo abaixo.{' '}
            <b>Não incluso</b> aparece no PDF em cinza, como disponível para
            contratar depois. <b>Fora</b> não aparece na proposta.
          </p>
          <div className="space-y-1.5">
            {(services.data ?? [])
              .filter((s) => s.id !== form.serviceId)
              .map((svc) => {
                const mod = form.modules.find((m) => m.serviceId === svc.id);
                const estado = !mod ? 'fora' : mod.incluso ? 'on' : 'off';
                const impl = svc.chargeLines?.find((c) => c.tipo === 'UNICA');
                const rec = svc.chargeLines?.find((c) => c.tipo !== 'UNICA');
                return (
                  <div
                    key={svc.id}
                    className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${
                      estado === 'on'
                        ? 'border-primary/40 bg-primary/5'
                        : estado === 'off'
                          ? 'border-border bg-muted/40'
                          : 'border-dashed border-input'
                    }`}
                  >
                    <div className="min-w-0">
                      <div
                        className={`truncate text-sm ${estado === 'off' ? 'text-muted-foreground line-through' : ''}`}
                      >
                        {svc.nome}
                      </div>
                      {(impl || rec) && (
                        <div className="text-xs text-muted-foreground">
                          {impl && `${formatBRL(Number(impl.valor))} implantação`}
                          {impl && rec && ' + '}
                          {rec &&
                            `${formatBRL(Number(rec.valor))}${rec.tipo === 'MENSAL' ? '/mês' : '/ano'}`}
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {(
                        [
                          ['fora', 'Fora', null],
                          ['on', '✓ Incluso', true],
                          ['off', '✗ Não incluso', false],
                        ] as const
                      ).map(([key, label, val]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleModule(svc, val)}
                          className={`rounded px-2 py-1 text-xs transition-colors ${
                            estado === key
                              ? 'bg-primary text-primary-foreground'
                              : 'border border-input text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </fieldset>

        <fieldset className="space-y-2 rounded-md border border-border p-3">
          <legend className="px-2 text-xs font-medium uppercase text-muted-foreground">
            Linhas de cobrança (substituem o valor principal no PDF se preenchidas)
          </legend>
          <p className="text-xs text-muted-foreground">
            Use para cobrar implantação + mensalidade + taxas separados. Se vazio,
            usa o "Valor" acima como valor único.
          </p>
          {form.chargeLines.map((line, idx) => (
            <div key={idx} className="space-y-1">
              <div className="grid grid-cols-[2fr_100px_120px_30px] gap-2">
                <input
                  className="input"
                  placeholder="Descrição (ex.: Implantação)"
                  value={line.descricao}
                  onChange={(e) => {
                    const copy = [...form.chargeLines];
                    copy[idx] = { ...line, descricao: e.target.value };
                    setForm((s) => ({ ...s, chargeLines: copy }));
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  className={`input ${line.valor < 0 ? 'text-destructive font-medium' : ''}`}
                  placeholder="Valor"
                  value={line.valor || ''}
                  onChange={(e) => {
                    const copy = [...form.chargeLines];
                    copy[idx] = { ...line, valor: Number(e.target.value) || 0 };
                    setForm((s) => ({ ...s, chargeLines: copy }));
                  }}
                />
                <select
                  className="input"
                  value={line.tipo}
                  onChange={(e) => {
                    const copy = [...form.chargeLines];
                    copy[idx] = { ...line, tipo: e.target.value as ChargeLineKind };
                    setForm((s) => ({ ...s, chargeLines: copy }));
                  }}
                >
                  <option value="UNICA">única</option>
                  <option value="MENSAL">/mês</option>
                  <option value="ANUAL">/ano</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const copy = [...form.chargeLines];
                    copy.splice(idx, 1);
                    setForm((s) => ({ ...s, chargeLines: copy }));
                  }}
                  className="rounded-md border border-input p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <input
                className="input"
                placeholder="Nota de cálculo (opcional, ex.: 10% sobre mensalidade)"
                value={line.notaCalculo}
                onChange={(e) => {
                  const copy = [...form.chargeLines];
                  copy[idx] = { ...line, notaCalculo: e.target.value };
                  setForm((s) => ({ ...s, chargeLines: copy }));
                }}
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={line.informativa}
                  onChange={(e) => {
                    const copy = [...form.chargeLines];
                    copy[idx] = { ...line, informativa: e.target.checked };
                    setForm((s) => ({ ...s, chargeLines: copy }));
                  }}
                />
                Só referência — mostra o valor mas <b>não soma</b> nos totais
              </label>
              {line.informativa && (
                <input
                  className="input"
                  placeholder="Selo ao lado da descrição (opcional, ex.: já vigente)"
                  value={line.selo}
                  onChange={(e) => {
                    const copy = [...form.chargeLines];
                    copy[idx] = { ...line, selo: e.target.value };
                    setForm((s) => ({ ...s, chargeLines: copy }));
                  }}
                />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() =>
                setForm((s) => ({
                  ...s,
                  chargeLines: [
                    ...s.chargeLines,
                    {
                      descricao: '',
                      valor: 0,
                      tipo: 'MENSAL',
                      notaCalculo: '',
                      informativa: false,
                      selo: '',
                    },
                  ],
                }))
              }
              className="flex items-center gap-1 rounded-md border border-dashed border-input px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent"
            >
              <Plus className="h-3.5 w-3.5" /> Adicionar linha
            </button>
            <button
              type="button"
              onClick={() =>
                setForm((s) => ({
                  ...s,
                  chargeLines: [
                    ...s.chargeLines,
                    {
                      descricao: 'Desconto',
                      valor: 0,
                      tipo: 'UNICA',
                      notaCalculo: '',
                      informativa: false,
                      selo: '',
                    },
                  ],
                }))
              }
              className="flex items-center gap-1 rounded-md border border-dashed border-destructive/30 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10"
            >
              <Plus className="h-3.5 w-3.5" /> Adicionar desconto
            </button>
            <button
              type="button"
              onClick={() =>
                setForm((s) => ({
                  ...s,
                  chargeLines: [
                    ...s.chargeLines,
                    {
                      descricao: '',
                      valor: 0,
                      tipo: 'MENSAL',
                      notaCalculo: '',
                      informativa: true,
                      selo: '',
                    },
                  ],
                }))
              }
              className="flex items-center gap-1 rounded-md border border-dashed border-amber-400/50 px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-50"
            >
              <Plus className="h-3.5 w-3.5" /> Adicionar referência
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            💡 Para um desconto, preencha o valor com sinal negativo (ex.: <code>-1200</code>).
          </p>
        </fieldset>

        <Field label="Observações internas">
          <textarea
            rows={2}
            className="input"
            value={form.observacoesInternas}
            onChange={(e) => setForm((s) => ({ ...s, observacoesInternas: e.target.value }))}
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {save.isPending
              ? isEdit
                ? 'Salvando…'
                : 'Criando…'
              : isEdit
                ? 'Salvar alterações'
                : 'Criar proposta'}
          </button>
        </div>
      </form>
      <Style />
    </Dialog>
  );
}

// ============================ Accept dialog ============================

function AcceptDialog({
  proposal,
  onClose,
  onDone,
}: {
  proposal: Proposal;
  onClose: () => void;
  onDone: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const default10 = new Date();
  default10.setDate(default10.getDate() + 10);
  const [vencimento, setVencimento] = useState(default10.toISOString().slice(0, 10));
  const [acceptedAt, setAcceptedAt] = useState(today);
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: () =>
      apiFetch(`/proposals/${proposal.id}/accept`, {
        method: 'POST',
        body: JSON.stringify({ vencimento, acceptedAt }),
      }),
    onSuccess: onDone,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  return (
    <Dialog title={`Aceitar proposta #${proposal.numero}`} onClose={onClose}>
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          accept.mutate();
        }}
        className="space-y-4"
      >
        <p className="text-sm text-muted-foreground">
          Ao aceitar, será criado um lançamento financeiro de{' '}
          <strong>{formatBRL(Number(proposal.valorPrincipal))}</strong> com vencimento abaixo.
          Depois você poderá emitir a nota fiscal direto do Financeiro.
        </p>
        <Field label="Data do aceite">
          <input
            type="date"
            className="input"
            value={acceptedAt}
            onChange={(e) => setAcceptedAt(e.target.value)}
          />
        </Field>
        <Field label="Vencimento do lançamento *">
          <input
            type="date"
            required
            className="input"
            value={vencimento}
            onChange={(e) => setVencimento(e.target.value)}
          />
        </Field>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={accept.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {accept.isPending ? 'Aceitando…' : 'Aceitar e gerar lançamento'}
          </button>
        </div>
      </form>
      <Style />
    </Dialog>
  );
}

// ============================ Reject dialog ============================

function RejectDialog({
  proposal,
  onClose,
  onDone,
}: {
  proposal: Proposal;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const reject = useMutation({
    mutationFn: () =>
      apiFetch(`/proposals/${proposal.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: onDone,
  });
  return (
    <Dialog title={`Recusar proposta #${proposal.numero}`} onClose={onClose}>
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          reject.mutate();
        }}
        className="space-y-4"
      >
        <Field label="Motivo (opcional)">
          <textarea
            rows={3}
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={reject.isPending}
            className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {reject.isPending ? 'Recusando…' : 'Confirmar recusa'}
          </button>
        </div>
      </form>
      <Style />
    </Dialog>
  );
}

// ============================ Revise (contra-proposta) ============================

function ReviseDialog({
  proposal,
  onClose,
  onDone,
}: {
  proposal: Proposal;
  onClose: () => void;
  onDone: () => void;
}) {
  const valorAtual = Number(proposal.valorPrincipal);
  const [mode, setMode] = useState<'percent' | 'absolute'>('percent');
  const [percent, setPercent] = useState(-10);
  const [novoValor, setNovoValor] = useState(valorAtual);
  const [validadeDias, setValidadeDias] = useState(proposal.validadeDias);
  const [error, setError] = useState<string | null>(null);

  const previewValor =
    mode === 'percent'
      ? Math.round(valorAtual * (1 + percent / 100) * 100) / 100
      : Math.round(novoValor * 100) / 100;

  const revise = useMutation({
    mutationFn: () =>
      apiFetch(`/proposals/${proposal.id}/revise`, {
        method: 'POST',
        body: JSON.stringify({
          novoValor: previewValor,
          validadeDias,
        }),
      }),
    onSuccess: onDone,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  return (
    <Dialog title={`Contra-proposta da #${proposal.numero}`} onClose={onClose}>
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          setError(null);
          revise.mutate();
        }}
        className="space-y-4"
      >
        <p className="text-sm text-muted-foreground">
          Cria uma nova proposta vinculada à #{proposal.numero}.
          A original é marcada como <strong>Revisada</strong>.
        </p>
        <div className="rounded-md bg-muted/30 p-3 text-sm">
          Valor atual da proposta: <strong>{formatBRL(valorAtual)}</strong>
          {proposal.valorUnidade && <span className="text-xs"> {proposal.valorUnidade}</span>}
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={mode === 'percent'} onChange={() => setMode('percent')} />
            Aplicar percentual (ex.: -10 = 10% de desconto)
          </label>
          {mode === 'percent' && (
            <input
              type="number"
              step="0.01"
              className="input"
              value={percent}
              onChange={(e) => setPercent(Number(e.target.value) || 0)}
            />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={mode === 'absolute'} onChange={() => setMode('absolute')} />
            Definir novo valor
          </label>
          {mode === 'absolute' && (
            <input
              type="number"
              step="0.01"
              min={0.01}
              className="input"
              value={novoValor || ''}
              onChange={(e) => setNovoValor(Number(e.target.value) || 0)}
            />
          )}
        </div>
        <Field label="Nova validade (dias)">
          <input
            type="number"
            min={1}
            className="input"
            value={validadeDias}
            onChange={(e) => setValidadeDias(Number(e.target.value) || 30)}
          />
        </Field>
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm flex justify-between">
          <span>Novo valor da proposta:</span>
          <strong>{formatBRL(previewValor)}</strong>
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={revise.isPending || previewValor <= 0}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {revise.isPending ? 'Criando…' : 'Criar contra-proposta'}
          </button>
        </div>
      </form>
      <Style />
    </Dialog>
  );
}

// ============================ helpers ============================

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        border: 1px solid hsl(var(--input));
        background: hsl(var(--background));
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
      }
      .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
    `}</style>
  );
}
